<template>
  <div class="reports-section standard-page">
    <v-tabs
      left
      v-model="selectedMenuIndex"
      background-color="transparent"
      @change="handleTabChange"
    >
      <v-tab
        v-for="key in menuKeys"
        :key="key"
      >
        <v-icon left>
          {{ iconsMap[key] }}
        </v-icon>

        {{ typesMap[key] }}
      </v-tab>
    </v-tabs>

    <!-- Data Table -->
    <v-data-table
      :headers="tableHeaders"
      :items="tableItems"
      class="elevation-1 custom-table--report-lists mt-4"
    >
      <!-- For the profile view, let's show a search input -->
      <template v-slot:header.preview>
        <div class="contain-max-width">
          <v-text-field
            v-model="filterAccount"
            color="white"
            label="Search"
            hide-details
            outlined
            dense
            dark
          />
        </div>
      </template>

      <!-- Show the platform icon -->
      <template v-slot:item.platform="{ item }">
        <!-- Show the icon for platform -->
        <v-img
          width="30"
          height="30"
          max-width="30"
          :src="platformIconMap[item.platform]"
        />
      </template>

      <!-- Show human readable date format -->
      <template v-slot:item.created_at="{ item }">
        {{ dayjs(item.created_at).format("lll") }}
      </template>

      <!-- Show a little bit of preview -->
      <template v-slot:item.preview="{ item }">
        <!-- Show the profile chip, if possible -->
        <profile-chip
          v-if="item.preview"
          :data="item.preview"
          :platform="item.platform"
        />

        <!-- Otherwise -->
        <span v-else>
          {{ item.fullname || item.username }}
        </span>
      </template>

      <!-- Show a little bit of preview -->
      <template v-slot:item.profiles="{ item }">
        <div class="d-flex flex-wrap">
          <!-- Show the profile chip, if possible -->
          <profile-chip
            v-for="(profile, index) in item.profiles"
            :data="profile"
            :key="'preview' + index"
            :platform="item.platform"
            class="mr-4 my-2"
          />
        </div>
      </template>

      <!-- For the last/action column -->
      <template v-slot:item.filetype="{ item }">
        <!-- Show the default CTA -->
        <v-btn
          color="primary"
          :loading="loadingItems.includes(item.symbol)"
          :disabled="loadingItems.includes(item.symbol)"
          @click="triggerDownload(item)"
          width="170"
          depressed
          small
        >
          Download {{ item.filetype === "csv" ? "XLSX" : item.filetype.toUpperCase() }}
          <v-icon right>
            download
            </v-icon>
        </v-btn>

        <!-- Show a view report CTA in case of "influencer-insights" -->
        <v-btn
          v-if="isInfluencerInsightSelected"

          small
          depressed
          class="ml-3"
          color="primary"
          @click="$router.push({ name: 'ProfileReport', params: { platform: item.platform, username: item.username }, query: { account_id: item.accountId } })"
        >
          View
          <v-icon right>
            chevron_right
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// Import node package
import download from "downloadjs"

// Import helper function to generate XLSX file
import downloadCSVtoXLSX from "@/helpers/downloadCSVtoXLSX"

// Import child component
import ProfileChip from "@/blocks/common/ProfileChip.vue"

// Export the SFC
export default {
  // Name of the component
  name: "Reports",

  // Register child components
  components: {
    ProfileChip
  },

  // Define local data variables
  data: () => ({
    // The tab menu that's been selected
    selectedMenuIndex: 0,

    // The buttons that have been clicked and are being loaded
    loadingItems: [],
    // The percentage of download progress
    downloadProgressMap: [],

    // To filter out the profiles by
    filterAccount: "",

    // The tab options
    typesMap: {
      "influencer-insight": "Influencer Insight",
      "paid-collaborations": "Paid Collaborations",
      "influencer-discovery": "Influencer Discovery",
      "audience-overlap": "Audience Overlap"
    },

    // The icons to show in the tab item
    iconsMap: {
      "influencer-insight": "insights",
      "paid-collaborations": "query_stats",
      "influencer-discovery": "person_search",
      "audience-overlap": "picture_in_picture"
    }
  }),

  // Define computable data variables
  computed: {
    /**
     * Respond whether to show unlocked accounts or generated reports
     *
     * @returns {Boolean}
     */
    isInfluencerInsightSelected() {
      return this.selectedMenuIndex === Object.keys(this.typesMap).findIndex((search) => search === "influencer-insight")
    },

    /**
     * Respond whether to show unlocked accounts or generated reports
     *
     * @returns {Boolean}
     */
    isAudienceOverlapSelected() {
      return this.selectedMenuIndex === Object.keys(this.typesMap).findIndex((search) => search === "audience-overlap")
    },

    /**
     * The object keys for the menu options
     *
     * @returns {Array}
     */
    menuKeys() {
      return Object.keys(this.typesMap)
    },

    /**
     * Get the network response items from store
     *
     * @returns {Object}
     */
    resultItems() {
      return this.$store.getters["reports/items"]
    },

    /**
     * Get the generated reports array from store
     *
     * @returns {Array}
     */
    reportsArray() {
      return this.resultItems.reports.map((item) => ({ ...item, symbol: Symbol() }))
    },

    /**
     * Get the unlocked profiles array from store
     *
     * @returns {Array}
     */
    accountsArray() {
      return this.resultItems.accounts.map((item) => ({ ...item, symbol: Symbol() }))
    },

    /**
     * Get the audience overlap records from store
     *
     * @returns {Array}
     */
    audienceOverlapsArray() {
      return this.resultItems.audienceOverlaps.map((item) => ({ ...item, symbol: Symbol() }))
    },

    /**
     * The headers to show in the table
     *
     * @returns {Array}
     */
    tableHeaders() {
      // If we have to show the unlocked profiles
      if (this.isInfluencerInsightSelected) {
        return [
          {
            text: "",
            value: "platform",
            sortable: true,
          },
          {
            text: "Created",
            value: "created_at",
            sortable: true,
            sort: (a, b) => {
              return dayjs(a).unix() - dayjs(b).unix()
            }
          },
          {
            text: "Profile",
            value: "preview",
            sortable: false
          },
          {
            text: "Action",
            value: "filetype",
            sortable: false,
            align: "end"
          }
        ]
      }

      // If we have to show the unlocked profiles
      if (this.isAudienceOverlapSelected) {
        return [
          {
            text: "",
            value: "platform",
            sortable: true,
          },
          {
            text: "Created",
            value: "created_at",
            sortable: true,
            sort: (a, b) => {
              return dayjs(a).unix() - dayjs(b).unix()
            }
          },
          {
            text: "Profiles",
            value: "profiles",
            sortable: false
          },
          {
            text: "Action",
            value: "filetype",
            sortable: false,
            align: "end"
          }
        ]
      }

      // Otherwise
      return [
        {
          text: "Title",
          value: "title",
          sortable: false,
        },
        {
          text: "Created",
          value: "created_at",
          sortable: false,
        },
        {
          text: "Action",
          value: "filetype",
          sortable: false,
          align: "end"
        }
      ]
    },

    /**
     * The rows to show in the table
     *
     * @returns {Array}
     */
    tableItems() {
      // Get the current selected tab option
      const selectedType = Object.keys(this.typesMap)[this.selectedMenuIndex]

      // For influencer insight reports, show a customized mapped data structure
      if (this.isInfluencerInsightSelected) {
        return this.accountsArray
          .map((item) => ({
            id: item.id,
            symbol: item.symbol,
            filetype: "pdf",
            created_at: item.created_at,
            username: item.username || item.account_id,
            fullname: item.preview?.fullname || item.fullname,
            platform: item.platform,
            accountId: item.account_id,
            preview: item.preview
          }))
          .filter((item) => {
            return (item.fullname || "").toLowerCase().search(this.filterAccount.toLowerCase()) > -1 || (item.username || "").toLowerCase().search(this.filterAccount.toLowerCase()) > -1
          })
      }

      // For audience overlap reports, show a customized mapped data structure
      if (this.isAudienceOverlapSelected) {
        return this.audienceOverlapsArray
          .map((item) => ({
            id: item.id,
            symbol: item.symbol,
            filetype: "pdf",
            created_at: item.created_at,
            platform: item.platform,
            profiles: item.items.map((_) => _.preview)
          }))
          .filter((item) => {
            return (item.fullname || "").toLowerCase().search(this.filterAccount.toLowerCase()) > -1 || (item.username || "").toLowerCase().search(this.filterAccount.toLowerCase()) > -1
          })
      }

      // For other types, show the data as it is
      return this.reportsArray.filter((item) => item.type === selectedType)
    }
  },

  // Define Options API method functions
  methods: {
    /**
     * When the tab changes, update the query parameter
     *
     * @returns {void}
     */
    handleTabChange() {
      this.$router.replace({
        name: this.$route.name,
        query: {
          tab: this.menuKeys[this.selectedMenuIndex]
        }
      })
    },

    /**
     * Download the report file from API's response
     *
     * @param {Object} item | The report model
     * @returns {void}
    */
    async downloadFile(item) {
      try {
        const headersMap = {
          csv: "text/csv",
          pdf: "application/pdf",
          json: "application/json",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        }

        // find the items to skip
        const response = await axios({
          url: "/api/reports/" + item.id,
          method: "POST",
          responseType: 'arraybuffer', // since we'd be downloading it
          headers: {
            'Accept': Object.values(headersMap).join(", ")
          }
        })

        if (item.filetype === "csv") {
          downloadCSVtoXLSX({
            data: (new window.TextDecoder()).decode(response.data),
            filename: `${item.title} - ${process.env.VUE_APP_NAME}.xlsx`
            // filename: `${this.typesMap[item.type]} ${item.title} - ${process.env.VUE_APP_NAME}.xlsx`
          })
        }
        else {
          download(response.data, `${item.title} - ${process.env.VUE_APP_NAME}.${item.filetype}`, headersMap[item.filetype])
          // download(response.data, `${this.typesMap[item.type]} ${item.title} - ${process.env.VUE_APP_NAME}.${item.filetype}`, headersMap[item.filetype])
        }
      } catch (error) {
        logger({ type: "Error while downloading report", error })

        this.$store.dispatch("toasts/add", { text: "An error occurred." })
      } finally {
        const index = this.loadingItems.findIndex((symbol) => symbol === item.symbol)
        this.loadingItems.splice(index, 1)
      }
    },

    /**
     * Handle the download CTA click from the template
     *
     * @param {Object} item
     * @returns {void}
     */
    async triggerDownload(item) {
      this.loadingItems.push(item.symbol)

      if (this.isInfluencerInsightSelected) {
        const triggerId = String(Date.now())

        // Add this to loaders map to be able to hide progress
        this.downloadProgressMap.push({
          triggerId,
          symbol: item.symbol
        })

        // Make the network request
        await this.$store.dispatch("userProfile/downloadProfile", {
          triggerId,
          platform: item.platform,
          username: item.username,
          accountId: item.accountId
        })
      }
      else if (this.isAudienceOverlapSelected) {
        const triggerId = String(Date.now())

        // Add this to loaders map to be able to hide progress
        this.downloadProgressMap.push({
          triggerId,
          symbol: item.symbol
        })

        // Make the network request
        await this.$store.dispatch("audienceOverlap/download", {
          triggerId,
          reportId: item.id
        })
      }
      else {
        this.downloadFile(item)
      }
    },

    /**
     * Called from window's event listeners
     *
     * @param {Event} event
     * @returns {void}
     */
    stopDownloadProgressBar(event) {
      const foundItem = this.downloadProgressMap.find((search) => search.triggerId === event.detail.data?.misc?.triggerId)

      if (foundItem) {
        const index = this.loadingItems.findIndex((search) => search === foundItem.symbol)

        if (index !== -1) {
          this.loadingItems.splice(index, 1)
        }
      }
    }
  },

  /**
   * As soon as the template's been rendered
   *
   * @returns {void}
   */
  created() {
    // Fetch reports and unlocked profiles list from the API
    this.$store.dispatch("reports/fetchItems")

    // Keep listening for an event
    window.addEventListener("ReadyToDownload", this.stopDownloadProgressBar)

    // Now switch the tab if needed
    if (this.$route.query.tab) {
      // Find the index
      const index = this.menuKeys.findIndex((search) => search === this.$route.query.tab)

      // If found switch to it
      if (index !== -1) {
        this.selectedMenuIndex = index
      }
    }
  },

  /**
   * Before this component is about to be removed
   *
   * @returns {void}
   */
  beforeDestroy() {
    // De-register the event listener
    window.removeEventListener("ReadyToDownload", this.stopDownloadProgressBar)
  }
}
</script>

<style lang="stylus">
.reports-section
  .v-slide-group__prev.v-slide-group__prev--disabled
    display none !important

  .contain-max-width
    max-width 24em

    input, label
      font-weight 500 !important
</style>
